import { useCallback } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { PlanCategory, PlanDisplayCategory, PlanSourceOrigin, PlanStatus, SystemDomain } from '@/client';
import { parseFilterParam, toFilterParam, type PlanFilterSelection } from '@/components/maintenance/model/planFilters';
import { occurrenceKey } from '@/components/maintenance/model/planHistory';
import { isScheduledStatus } from '@/components/maintenance/model/planVocabulary';
import { isFilterablePlanCategory } from '@/components/maintenance/model/planCategoryLabels';
import { PropertyRouteTo } from '@/routes';

/** The file-route id `useSearch` reads from, which is not the URL path `navigate` writes to. */
const SCHEDULE_ROUTE_ID = '/_authenticated/property/$propertyPublicId/schedule' as const;

/**
 * Which part of the screen is in view.
 *
 * Scheduled and Unscheduled split on `review.placement` — the household's own answer about what
 * they want reminding about. Whether a card carries a date is not a tab boundary: a card they
 * deprioritised keeps its date and still belongs under Unscheduled. That shape is a lens within
 * the tab, not a surface of its own.
 *
 * Absent means Scheduled, so a plain link opens it.
 */
export const ScheduleTabs = { Scheduled: undefined, Unscheduled: 'unscheduled', History: 'history' } as const;

/**
 * Which shape of unscheduled work the Unscheduled tab is showing.
 *
 * Absent means all of it, so the tab opens on everything the household deferred rather than on
 * half of it.
 */
export const UnscheduledLenses = { All: 'all', Dated: 'dated', AsNeeded: 'as-needed' } as const;
export type UnscheduledLens = (typeof UnscheduledLenses)[keyof typeof UnscheduledLenses];

/**
 * Which kind of obligation the Scheduled tab is showing.
 *
 * A lens over `category`, not the `category` facet filter: that one is a server query param whose
 * chips are drawn beside it, and two controls writing one parameter would fight. This splits the
 * cards the tab already holds, exactly as the Unscheduled lens splits its own — placement is a
 * client-side answer, so the tab's set is client-derived either way.
 *
 * The narrowing values are `FILTERABLE_PLAN_CATEGORIES`, so the lens says Maintenance, Warranties
 * and Compliance in the same words as every other category control. `awareness` has no lens for
 * the reason stated there: it is a transport bucket rather than one coherent choice, and its cards
 * stay reachable under All.
 *
 * Absent means all of it, so the tab opens on everything scheduled rather than a slice.
 */
export const ScheduledLenses = {
	All: 'all',
	Maintenance: 'maintenance',
	Warranty: 'warranty',
	Compliance: 'compliance',
} as const;
export type ScheduledLens = (typeof ScheduledLenses)[keyof typeof ScheduledLenses];

/** What the URL says to open the review queue. Absent means nothing was asked for. */
const REVIEW_REQUESTED = 'open';

/** Every tab the URL can name, which is every value above bar the absent one. */
type ScheduleTab = 'unscheduled' | 'history' | undefined;

/**
 * Every way the schedule's URL changes, in one place.
 *
 * It exists because the alternative failed in practice. Each caller used to build a whole search
 * object, and TanStack replaces rather than merges, so a navigation silently dropped every parameter
 * it did not happen to mention — opening a card wiped the facet filters, and closing it wiped the
 * open tab. Those are not bugs you notice while writing the call; you notice them a week later as a
 * list that quietly reset.
 *
 * So no caller here names a whole search. Each says only what it changes, and everything else is
 * carried. Removing a parameter is spelled `undefined`, which is the one way to drop something and
 * has to be written on purpose.
 */
export const useScheduleSearch = (propertyPublicId: string) => {
	const search = useSearch({ from: SCHEDULE_ROUTE_ID });
	const navigate = useNavigate();

	const update = useCallback(
		(
			patch: Partial<{
				planNodeId: string | undefined;
				occurrence: string | undefined;
				tab: ScheduleTab;
				category: string | undefined;
				displayCategory: string | undefined;
				status: string | undefined;
				origin: string | undefined;
				systemDomain: string | undefined;
				show: Exclude<UnscheduledLens, typeof UnscheduledLenses.All> | undefined;
				kind: Exclude<ScheduledLens, typeof ScheduledLenses.All> | undefined;
				review: typeof REVIEW_REQUESTED | undefined;
			}>,
			options: { replace?: boolean } = {},
		) =>
			navigate({
				to: PropertyRouteTo.schedule,
				params: { propertyPublicId },
				// Merged off this route's own search rather than the reducer's argument, which the
				// router widens to a union of every route's params and so cannot be spread back into
				// this one. Recreated whenever the URL changes, so it never carries a stale copy.
				search: { ...search, ...patch },
				replace: options.replace,
			}),
		[navigate, propertyPublicId, search],
	);

	const filters = {
		category: parseFilterParam<PlanCategory>(search.category).filter(isFilterablePlanCategory),
		displayCategory: parseFilterParam<PlanDisplayCategory>(search.displayCategory),
		status: parseFilterParam<PlanStatus>(search.status).filter(isScheduledStatus),
		sourceOrigin: parseFilterParam<PlanSourceOrigin>(search.origin),
		systemDomain: parseFilterParam<SystemDomain>(search.systemDomain),
	};
	return {
		/** What the URL currently says. */
		tab: search.tab,
		planNodeId: search.planNodeId,
		occurrence: search.occurrence,
		filters,
		isHistory: search.tab === ScheduleTabs.History,
		/** A link asked for the review queue — see `ReviewQueueProvider`. */
		wantsReview: search.review === REVIEW_REQUESTED,
		isUnscheduled: search.tab === ScheduleTabs.Unscheduled,
		/** Everything deferred unless the URL narrows it, so the tab opens on the whole of itself. */
		unscheduledLens:
			search.show === UnscheduledLenses.Dated || search.show === UnscheduledLenses.AsNeeded
				? search.show
				: UnscheduledLenses.All,
		setUnscheduledLens: useCallback(
			(lens: UnscheduledLens) =>
				void update({ show: lens === UnscheduledLenses.All ? undefined : lens }, { replace: true }),
			[update],
		),
		/** Everything scheduled unless the URL narrows it, matching the Unscheduled lens beside it. */
		scheduledLens: isFilterablePlanCategory(search.kind ?? '')
			? (search.kind as ScheduledLens)
			: ScheduledLenses.All,
		setScheduledLens: useCallback(
			(lens: ScheduledLens) =>
				void update({ kind: lens === ScheduledLenses.All ? undefined : lens }, { replace: true }),
			[update],
		),
		hasFilters: Object.values(filters).some((selection) => selection.length > 0),

		/**
		 * Switches the list underneath, and closes whatever card was over it.
		 *
		 * A card belongs to the list it was opened from. Left standing across a tab press it describes
		 * something the new list does not contain — a past occurrence still on screen while Upcoming
		 * loads behind it — and nothing tells the reader they are looking at the tab they just left.
		 *
		 * The clearing lives here rather than in an effect watching `tab`, because a tab can also
		 * change *with* a card deliberately open: `showOccurrenceInHistory` is the way back from an
		 * entry to its row, and it moves the list while keeping the card. That path does not come
		 * through here, which is what makes this the safe place to drop the card.
		 */
		setTab: useCallback(
			(tab: ScheduleTab) =>
				// `show` goes with the card for the same reason: it narrows the Unscheduled list and
				// means nothing anywhere else, so carrying it leaves the URL describing a lens no tab is
				// applying.
				void update({ tab, planNodeId: undefined, occurrence: undefined, show: undefined }),
			[update],
		),

		setFilters: useCallback(
			(next: Partial<PlanFilterSelection>, options: { replace?: boolean } = {}) =>
				void update(
					{
						category: toFilterParam(next.category ?? filters.category),
						displayCategory: toFilterParam(next.displayCategory ?? filters.displayCategory),
						status: toFilterParam(next.status ?? filters.status),
						origin: toFilterParam(next.sourceOrigin ?? filters.sourceOrigin),
						systemDomain: toFilterParam(next.systemDomain ?? filters.systemDomain),
					},
					// A default the screen applied for you is not a step to press Back out of.
					{ replace: options.replace },
				),
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[update, search.category, search.displayCategory, search.status, search.origin, search.systemDomain],
		),

		/**
		 * Opens a card at the occasion it is living on. Whatever tab and lenses were showing stay showing.
		 *
		 * Drops `occurrence` rather than carrying it, which is the one parameter that must not survive:
		 * left behind by an earlier visit to the record, it would reopen the card at a date the
		 * household did not ask for — and on the same card it named, silently.
		 */
		openCard: useCallback(
			(planNodeId: string, options: { replace?: boolean } = {}) =>
				void update({ planNodeId, occurrence: undefined }, options),
			[update],
		),

		/** Opens a card at one of its past instances, which its header and Task tab then describe. */
		openOccurrenceCard: useCallback(
			(nodeId: string, date: string) =>
				void update({ planNodeId: nodeId, occurrence: occurrenceKey(nodeId, date) }),
			[update],
		),

		/**
		 * Shows a dated instance in the record, keeping its card open over it.
		 *
		 * The return leg of `openOccurrenceCard`: the card stays, the schedule behind it switches to
		 * History, and the row is marked there — so closing the card lands on the row rather than on
		 * whatever list happened to be underneath.
		 */
		showOccurrenceInHistory: useCallback(
			(nodeId: string, date: string) =>
				void update({
					planNodeId: nodeId,
					occurrence: occurrenceKey(nodeId, date),
					tab: ScheduleTabs.History,
				}),
			[update],
		),

		/** Closes the card and drops only what addressed it. The list underneath is the one you left. */
		closeCard: useCallback(
			() => update({ planNodeId: undefined, occurrence: undefined }, { replace: true }),
			[update],
		),
	};
};
